import { signInWithEmailAndPassword, getMultiFactorResolver, PhoneAuthProvider, PhoneMultiFactorGenerator, signInWithCustomToken, signOut, onAuthStateChanged, applyActionCode, verifyPasswordResetCode, confirmPasswordReset, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { executeRecaptcha } from '../../utils/recaptcha.js';
import { AuthError } from '../types/auth.js';
import { getLoginErrorMessage, getMfaErrorMessage, getCustomTokenErrorMessage, getEmailVerificationErrorMessage, getPasswordResetErrorMessage, getPasswordUpdateErrorMessage, getInvitationErrorMessage, isRequiresRecentLoginError } from '../utils/authErrorMessages.js';

/** Get reCAPTCHA site key from environment, throws if not configured */
function getRecaptchaSiteKey() {
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
        throw new Error('VITE_RECAPTCHA_SITE_KEY is not configured');
    }
    return siteKey;
}
/**
 * Service for handling authentication with Firebase
 * Uses email and invite token (as password) for authentication
 */
class AuthService {
    constructor(auth) {
        this.auth = auth;
    }
    /**
     * Sign in with email and password
     * @param email - User's email address
     * @param password - Password for authentication
     * @returns Promise with user and access token, OR MFA required response with resolver
     */
    async signIn({ email, password }) {
        try {
            // Use Firebase email/password authentication
            const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
            const user = userCredential.user;
            // Get the Firebase ID token which we'll use for our backend authentication
            const accessToken = await user.getIdToken();
            return {
                user,
                accessToken,
            };
        }
        catch (error) {
            // Check if MFA is required
            if (this.isMfaRequiredError(error)) {
                const resolver = getMultiFactorResolver(this.auth, error);
                return {
                    mfaRequired: true,
                    resolver,
                };
            }
            // Map Firebase errors to our AuthError format
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/unknown-error', getLoginErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Authentication failed');
        }
    }
    /**
     * Type guard to check if an error is an MFA-required error
     */
    isMfaRequiredError(error) {
        return (typeof error === 'object' &&
            error !== null &&
            'code' in error &&
            error.code === 'auth/multi-factor-auth-required');
    }
    /**
     * Type guard to check if sign-in response requires MFA
     */
    isMfaRequiredResponse(response) {
        return 'mfaRequired' in response && response.mfaRequired === true;
    }
    /**
     * Send SMS verification code for MFA challenge
     * @param resolver - The MultiFactorResolver from the sign-in attempt
     * @param appVerifier - ApplicationVerifier instance (RecaptchaVerifier or test mock)
     * @param phoneInfoIndex - Index of the phone factor to use (default 0)
     * @returns Promise with verification ID
     */
    async sendMfaSmsCode(resolver, appVerifier, phoneInfoIndex = 0) {
        try {
            const phoneInfo = resolver.hints[phoneInfoIndex];
            const phoneAuthProvider = new PhoneAuthProvider(this.auth);
            const verificationId = await phoneAuthProvider.verifyPhoneNumber({
                multiFactorHint: phoneInfo,
                session: resolver.session,
            }, appVerifier);
            return verificationId;
        }
        catch (error) {
            const firebaseError = error;
            // Log the actual Firebase error for debugging
            console.error('sendMfaSmsCode Firebase error:', {
                code: firebaseError.code,
                message: firebaseError.message,
                fullError: error,
            });
            throw new AuthError(firebaseError.code || 'auth/mfa-sms-error', getMfaErrorMessage(firebaseError.code) || 'Failed to send SMS code');
        }
    }
    /**
     * Verify SMS code and complete MFA challenge
     * @param resolver - The MultiFactorResolver from the sign-in attempt
     * @param verificationId - The verification ID from sendMfaSmsCode
     * @param verificationCode - The code entered by the user
     * @returns Promise with user and access token
     */
    async verifyMfaSmsCode(resolver, verificationId, verificationCode) {
        try {
            const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
            const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
            const user = userCredential.user;
            const accessToken = await user.getIdToken();
            return {
                user,
                accessToken,
            };
        }
        catch (error) {
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/mfa-verify-error', getMfaErrorMessage(firebaseError.code) || 'Failed to verify MFA code');
        }
    }
    /**
     * Sign in with custom token
     * @param token - Custom Firebase token
     * @returns Promise with user and access token
     */
    async signInWithCustomToken({ token }) {
        try {
            // Use Firebase custom token authentication
            const userCredential = await signInWithCustomToken(this.auth, token);
            const user = userCredential.user;
            // Get the Firebase ID token which we'll use for our backend authentication
            const accessToken = await user.getIdToken();
            return {
                user,
                accessToken,
            };
        }
        catch (error) {
            // Map Firebase errors to our AuthError format
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/unknown-error', getCustomTokenErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Custom token authentication failed');
        }
    }
    /**
     * Sign out the current user
     */
    async signOut() {
        try {
            await signOut(this.auth);
        }
        catch (error) {
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/sign-out-error', firebaseError.message || 'Failed to sign out');
        }
    }
    /**
     * Get the current user's access token
     * @param forceRefresh - Whether to force refresh the token
     * @returns Promise with the access token or null if not authenticated
     */
    async getCurrentUserToken(forceRefresh = false) {
        const user = this.auth.currentUser;
        if (!user)
            return null;
        try {
            return await user.getIdToken(forceRefresh);
        }
        catch (error) {
            console.error('Failed to get user token:', error);
            return null;
        }
    }
    /**
     * Get the current authenticated user
     * @returns Current user or null if not authenticated
     */
    getCurrentUser() {
        return this.auth.currentUser;
    }
    /**
     * Listen to authentication state changes
     * @param callback - Function to call when auth state changes
     * @returns Unsubscribe function
     */
    onAuthStateChanged(callback) {
        return onAuthStateChanged(this.auth, callback);
    }
    /**
     * Send a password reset email to the user via backend
     * Uses custom branded emails via Customer.io instead of Firebase default emails
     *
     * @param email - User's email address
     * @param apiClient - API client for making backend requests
     * @param endpoint - Backend endpoint for password reset (e.g., '/auth_emails/password-reset')
     * @param continueUrl - Optional URL where user lands after clicking the link (e.g., app usermgmt)
     */
    async sendPasswordResetEmail(email, apiClient, endpoint, continueUrl) {
        try {
            // Execute reCAPTCHA challenge
            const recaptchaToken = await executeRecaptcha(getRecaptchaSiteKey(), 'reset_password');
            // Call backend endpoint to send branded password reset email
            // Backend uses Firebase Admin SDK to generate official action link
            // and sends via Customer.io with Ekho branding
            await apiClient.post(endpoint, {
                email: email.toLowerCase(),
                continueUrl: continueUrl,
            }, {
                headers: {
                    'X-Recaptcha-Token': recaptchaToken,
                },
            });
            // Backend always returns { ok: true } to prevent account enumeration
            // So we don't need to check the response
        }
        catch (error) {
            // Network or server errors should be surfaced to user
            console.error('Failed to send password reset email:', error);
            throw new AuthError('auth/network-request-failed', error instanceof Error && error.message.includes('reCAPTCHA')
                ? 'reCAPTCHA verification failed. Please try again.'
                : 'Unable to send password reset email. Please try again later.');
        }
    }
    /**
     * Send email verification email via backend
     * Uses custom branded emails via Customer.io instead of Firebase default emails
     *
     * @param email - User's email address
     * @param apiClient - API client for making backend requests
     * @param endpoint - Backend endpoint for email verification (e.g., '/auth_emails/email-verification')
     * @param options - Optional params to include in the email link
     * @param options.continueUrl - URL where user lands after clicking the link
     * @param options.inviteToken - Invite token for InviteAuthGuard
     * @param options.inviteEmail - Invite email for InviteAuthGuard
     */
    async sendEmailVerificationEmail(email, apiClient, endpoint, options) {
        try {
            // Execute reCAPTCHA challenge
            const recaptchaToken = await executeRecaptcha(getRecaptchaSiteKey(), 'email_verification');
            // Call backend endpoint to send branded email verification email
            await apiClient.post(endpoint, {
                email: email.toLowerCase(),
                continueUrl: options?.continueUrl,
                inviteToken: options?.inviteToken,
                inviteEmail: options?.inviteEmail,
            }, {
                headers: {
                    'X-Recaptcha-Token': recaptchaToken,
                },
            });
            // Backend always returns { ok: true } to prevent account enumeration
        }
        catch (error) {
            console.error('Failed to send email verification email:', error);
            throw new AuthError('auth/network-request-failed', error instanceof Error && error.message.includes('reCAPTCHA')
                ? 'reCAPTCHA verification failed. Please try again.'
                : 'Unable to send verification email. Please try again later.');
        }
    }
    /**
     * Apply email verification action code
     * Verifies the user's email using Firebase oobCode from the verification link
     *
     * @param oobCode - The out-of-band code from the verification email link
     */
    async applyEmailVerificationCode(oobCode) {
        try {
            await applyActionCode(this.auth, oobCode);
        }
        catch (error) {
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/unknown-error', getEmailVerificationErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Failed to verify email');
        }
    }
    /**
     * Verify a password reset code is valid
     * @param oobCode - The out-of-band code from the reset email link
     * @returns The email address associated with the reset code
     */
    async verifyPasswordResetCode(oobCode) {
        try {
            return await verifyPasswordResetCode(this.auth, oobCode);
        }
        catch (error) {
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/unknown-error', getPasswordResetErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Invalid or expired reset link');
        }
    }
    /**
     * Complete the password reset process
     * @param oobCode - The out-of-band code from the reset email link
     * @param newPassword - The new password to set
     */
    async confirmPasswordReset(oobCode, newPassword) {
        try {
            await confirmPasswordReset(this.auth, oobCode, newPassword);
        }
        catch (error) {
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/unknown-error', getPasswordResetErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Failed to reset password');
        }
    }
    /**
     * Update password for the currently authenticated user
     * Used during onboarding when user sets their permanent password
     * @param newPassword - The new password to set
     */
    async updateCurrentUserPassword(newPassword, currentCredentials) {
        const user = this.auth.currentUser;
        if (!user) {
            throw new AuthError('auth/no-current-user', 'No authenticated user found. Please sign in again.');
        }
        try {
            await updatePassword(user, newPassword);
        }
        catch (error) {
            const firebaseError = error;
            if (firebaseError.code === 'auth/requires-recent-login') {
                if (!currentCredentials) {
                    throw new AuthError('auth/requires-recent-login', 'Your session has expired. Please refresh the page and try again.');
                }
                // Attempt to reauthenticate and retry the password update
                try {
                    const credential = EmailAuthProvider.credential(currentCredentials.email, currentCredentials.password);
                    await reauthenticateWithCredential(user, credential);
                    await updatePassword(user, newPassword);
                    return;
                }
                catch (retryError) {
                    const retryFirebaseError = retryError;
                    throw new AuthError(retryFirebaseError.code || 'auth/unknown-error', getPasswordUpdateErrorMessage(retryFirebaseError.code) ||
                        retryFirebaseError.message ||
                        'Failed to update password after reauthentication');
                }
            }
            throw new AuthError(firebaseError.code || 'auth/unknown-error', getPasswordUpdateErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Failed to update password');
        }
    }
    /**
     * Map Firebase invitation error codes to user-friendly messages
     * Used by InviteAuthGuard to display invite-specific error messages
     */
    getInvitationErrorMessage(errorCode) {
        return getInvitationErrorMessage(errorCode);
    }
    /**
     * Re-authenticate the current user with their password.
     * Used when Firebase requires a recent login for sensitive operations like MFA enrollment.
     * Unlike signIn(), this refreshes the existing user's credentials without creating a new session.
     */
    async reauthenticate(password) {
        const user = this.auth.currentUser;
        if (!user?.email) {
            throw new AuthError('auth/no-user', 'No authenticated user found');
        }
        try {
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);
        }
        catch (error) {
            const firebaseError = error;
            throw new AuthError(firebaseError.code || 'auth/unknown-error', getLoginErrorMessage(firebaseError.code) ||
                firebaseError.message ||
                'Re-authentication failed');
        }
    }
    /**
     * Check if an error is the requires-recent-login error.
     * Used to determine if user needs to re-authenticate before sensitive operations.
     */
    isRequiresRecentLoginError(error) {
        return isRequiresRecentLoginError(error);
    }
}

export { AuthService };
//# sourceMappingURL=authService.js.map
